import { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { TrendingUp, Shield, Activity, FileText, Check } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const metrics = [
  {
    icon: TrendingUp,
    label: 'Cost saved vs full-time hire',
    value: '$120K',
    trend: '+23%',
  },
  {
    icon: Shield,
    label: 'Risk items closed',
    value: '18',
    trend: '+5',
  },
  {
    icon: Activity,
    label: 'Uptime / availability',
    value: '99.97%',
    trend: '+0.02%',
  },
];

const reportFeatures = [
  'Weekly outcomes',
  'Blockers + next steps',
  'Renewal recommendations',
];

export default function ImpactSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const metricsRef = useRef<(HTMLDivElement | null)[]>([]);
  const reportRef = useRef<HTMLDivElement>(null);
  const trendRefs = useRef<(SVGPathElement | null)[]>([]);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // Title animation
      gsap.fromTo(
        titleRef.current,
        { y: 24, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          scrollTrigger: {
            trigger: titleRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Metrics cards
      metricsRef.current.forEach((metric, index) => {
        if (metric) {
          gsap.fromTo(
            metric,
            { y: 40, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.6,
              delay: index * 0.15,
              scrollTrigger: {
                trigger: metric,
                start: 'top 85%',
                toggleActions: 'play none none reverse',
              },
            }
          );
        }
      });

      // Trend lines draw
      trendRefs.current.forEach((path) => {
        if (path) {
          const length = path.getTotalLength();
          gsap.set(path, {
            strokeDasharray: length,
            strokeDashoffset: length,
          });
          gsap.to(path, {
            strokeDashoffset: 0,
            duration: 1,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: path,
              start: 'top 80%',
              toggleActions: 'play none none reverse',
            },
          });
        }
      });

      // Report panel
      gsap.fromTo(
        reportRef.current,
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          scrollTrigger: {
            trigger: reportRef.current,
            start: 'top 85%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="section-flowing bg-navy-light relative z-[80]"
    >
      <div className="w-full px-6 lg:px-12 max-w-7xl mx-auto">
        {/* Title */}
        <div ref={titleRef} className="text-center mb-16 opacity-0">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-teal mb-4 block">
            Impact Tracking
          </span>
          <h2 className="font-heading text-h2 sm:text-h1 font-bold text-white max-w-3xl mx-auto">
            Measure what matters
          </h2>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {metrics.map((metric, index) => (
            <div
              key={metric.label}
              ref={(el) => { metricsRef.current[index] = el; }}
              className="surface-panel p-6 opacity-0"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-lg bg-teal/10 flex items-center justify-center">
                  <metric.icon className="w-5 h-5 text-teal" />
                </div>
                <span className="text-xs font-medium text-teal bg-teal/10 px-2 py-1 rounded-full">
                  {metric.trend}
                </span>
              </div>

              <p className="text-sm text-slate mb-2">{metric.label}</p>
              <p className="font-heading text-3xl font-bold text-white mb-4">
                {metric.value}
              </p>

              {/* Mini Trend Chart */}
              <svg
                className="w-full h-12"
                viewBox="0 0 200 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  ref={(el) => { trendRefs.current[index] = el; }}
                  d="M0 35 Q25 30, 50 25 T100 20 T150 15 T200 5"
                  stroke="#4CFFE1"
                  strokeWidth="2"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </div>
          ))}
        </div>

        {/* Report Preview Panel */}
        <div
          ref={reportRef}
          className="surface-panel p-8 opacity-0"
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-teal/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-teal" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-semibold text-white">
                  Auto-generated executive summaries
                </h3>
                <p className="text-sm text-slate">
                  Weekly reports delivered to your inbox
                </p>
              </div>
            </div>

            <ul className="flex flex-wrap gap-4">
              {reportFeatures.map((feature) => (
                <li key={feature} className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-teal" />
                  <span className="text-sm text-slate">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
